import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { supabase } from '../../lib/supabase';
import { ProfileModal } from '../profile/ProfileModal';
import { NetworkStatusIndicator } from '../ui/NetworkStatusIndicator';
import { createContextLogger } from '../../lib/logger';
import { getAdminId } from '../../utils/getAdminId';
import {
  Bell,
  Settings,
  LogOut,
  Sun,
  Moon,
  User,
  ChevronDown,
  Package,
  Menu,
  LogIn,
  LogOut as LogOutIcon,
  Receipt,
  FileX,
  PackagePlus,
  PackageMinus,
  Activity,
  Clock,
  DollarSign,
  Building2,
  Check,
  Loader2
} from 'lucide-react';

const log = createContextLogger('Navbar');

interface NavbarProps {
  onMenuClick?: () => void;
}

interface ActivityNotification {
  id: string;
  action_type: string;
  entity_type: string;
  description: string;
  metadata: any;
  created_at: string;
  user?: {
    id: string;
    full_name: string;
    email: string;
  };
}

export const Navbar: React.FC<NavbarProps> = ({ onMenuClick }) => {
  const { user, logout, companies, activeCompanyId, canSwitchCompany, switchCompany } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [userIcon, setUserIcon] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<ActivityNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastSeenAt, setLastSeenAt] = useState<string | null>(null);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [switchingCompanyId, setSwitchingCompanyId] = useState<string | null>(null);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const handleSwitchCompany = async (companyId: string) => {
    if (companyId === activeCompanyId || switchingCompanyId) return;
    setSwitchError(null);
    setSwitchingCompanyId(companyId);
    const result = await switchCompany(companyId);
    // On success the page reloads, so only a failure lands back here.
    if (!result.success) {
      setSwitchError(result.error || 'Unable to switch company.');
      setSwitchingCompanyId(null);
    }
  };

  useEffect(() => {
    const loadIcon = async () => {
      if (user?.id) {
        try {
          const { data: settings } = await supabase
            .from('user_settings')
            .select('avatar_data, last_seen_notification_at')
            .eq('user_id', user.id)
            .maybeSingle();

          if (settings?.avatar_data) {
            setUserIcon(settings.avatar_data);
          }
          if (settings?.last_seen_notification_at) {
            setLastSeenAt(settings.last_seen_notification_at);
          }
        } catch (error) {
          log.error('Failed to load avatar', { error: String(error) });
        }
      }
    };
    loadIcon();
    loadNotifications();

    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [user?.id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotifications]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowUserMenu(false);
    };

    if (showUserMenu) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showUserMenu]);

  useEffect(() => {
    if (notifications.length > 0 && lastSeenAt) {
      const unread = notifications.filter(n => new Date(n.created_at) > new Date(lastSeenAt)).length;
      setUnreadCount(unread);
    } else if (notifications.length > 0 && !lastSeenAt) {
      setUnreadCount(notifications.length);
    } else {
      setUnreadCount(0);
    }
  }, [notifications, lastSeenAt]);

  const loadNotifications = useCallback(async () => {
    if (!user?.id) return;

    try {
      const adminId = getAdminId(user);
      const { data: logs } = await supabase
        .from('activity_logs')
        .select(`
          *,
          user:users!activity_logs_user_id_fkey(id, full_name, email)
        `)
        .eq('admin_id', adminId)
        .order('created_at', { ascending: false })
        .limit(20);

      setNotifications(logs || []);
    } catch (error) {
      log.error('Failed to load notifications', { error: String(error) });
    }
  }, [user?.id, user?.admin_id]);

  const markNotificationsAsRead = async () => {
    if (!user?.id) return;

    const now = new Date().toISOString();
    try {
      await supabase
        .from('user_settings')
        .update({ last_seen_notification_at: now })
        .eq('user_id', user.id);

      setLastSeenAt(now);
      setUnreadCount(0);
    } catch (error) {
      log.error('Failed to mark notifications as read', { error: String(error) });
    }
  };

  const handleOpenNotifications = () => {
    const newState = !showNotifications;
    setShowNotifications(newState);
    if (newState && unreadCount > 0) {
      markNotificationsAsRead();
    }
  };

  const getActivityIcon = (actionType: string, description?: string) => {
    if (actionType === 'auth' && description?.includes('logged out')) return LogOutIcon;
    if (actionType === 'auth') return LogIn;
    if (actionType === 'invoice') return Receipt;
    if (actionType === 'invoice_cancelled') return FileX;
    if (actionType === 'incoming_shipment') return PackagePlus;
    if (actionType === 'outgoing_shipment') return PackageMinus;
    return Activity;
  };

  const getActivityColor = (actionType: string, description?: string) => {
    if (actionType === 'auth' && description?.includes('logged out')) {
      return { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-600 dark:text-amber-400' };
    }
    switch (actionType) {
      case 'auth':
        return { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-600 dark:text-green-400' };
      case 'invoice':
        return { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400' };
      case 'invoice_cancelled':
        return { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400' };
      case 'incoming_shipment':
        return { bg: 'bg-teal-100 dark:bg-teal-900/30', text: 'text-teal-600 dark:text-teal-400' };
      case 'outgoing_shipment':
        return { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-600 dark:text-orange-400' };
      default:
        return { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-600 dark:text-gray-400' };
    }
  };

  const getActivityLabel = (actionType: string, description?: string) => {
    if (actionType === 'auth' && description?.includes('logged out')) return 'Logout';
    if (actionType === 'auth') return 'Login';
    if (actionType === 'invoice') return 'Invoice';
    if (actionType === 'invoice_cancelled') return 'Cancelled';
    if (actionType === 'incoming_shipment') return 'Incoming';
    if (actionType === 'outgoing_shipment') return 'Outgoing';
    return actionType;
  };

  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const isUnread = (notification: ActivityNotification) => {
    if (!lastSeenAt) return true;
    return new Date(notification.created_at) > new Date(lastSeenAt);
  };

  const handleLogout = async () => {
    if (confirm('Are you sure you want to log out?')) {
      await logout();
      window.location.href = '/';
    }
  };

  return (
    <nav className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
      <div className="px-4 lg:px-6 py-3 lg:py-4">
        <div className="flex items-center justify-between">
          {/* Left side - Menu Button (mobile) + Network Status */}
          <div className="flex items-center space-x-3">
            {/* Mobile Menu Button */}
            <button
              onClick={onMenuClick}
              className="lg:hidden p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>

            {/* Logo for mobile */}
            <div className="flex items-center lg:hidden">
              <div className="w-8 h-8 rounded overflow-hidden flex items-center justify-center">
                <img
                  src="/ChatGPT Image Oct 8, 2025, 03_35_44 AM.png"
                  alt="GetInv"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            {/* Network Status - Professional Indicator */}
            <div className="hidden sm:flex items-center">
              <NetworkStatusIndicator />
            </div>
          </div>

          {/* Right side - User menu */}
          <div className="flex items-center space-x-2 lg:space-x-4">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDark ? <Sun className="h-4 w-4 lg:h-5 lg:w-5" /> : <Moon className="h-4 w-4 lg:h-5 lg:w-5" />}
            </button>

            {/* Notifications - Hidden on extra small screens */}
            <div className="relative hidden sm:block" ref={notificationRef}>
              <button
                onClick={handleOpenNotifications}
                className="notification-button p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 relative transition-colors"
              >
                <Bell className={`h-4 w-4 lg:h-5 lg:w-5 transition-colors ${unreadCount > 0 ? 'text-red-500' : ''}`} />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full animate-pulse">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="notification-dropdown absolute right-0 mt-2 w-96 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
                  {/* Header */}
                  <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-750 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Bell className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          Activity Log
                        </h3>
                      </div>
                      {unreadCount > 0 && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full">
                          {unreadCount} new
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Notification List */}
                  <div className="max-h-[400px] overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-12 text-center">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                          <Bell className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                        </div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                          No Activity Yet
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Activity will appear here as it happens
                        </p>
                      </div>
                    ) : (
                      <div>
                        {notifications.map((notification, index) => {
                          const IconComponent = getActivityIcon(notification.action_type, notification.description);
                          const colors = getActivityColor(notification.action_type, notification.description);
                          const unread = isUnread(notification);

                          return (
                            <div
                              key={notification.id || index}
                              className={`px-4 py-3 border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${unread ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}
                            >
                              <div className="flex items-start gap-3">
                                {/* Icon */}
                                <div className={`flex-shrink-0 w-9 h-9 rounded-lg ${colors.bg} flex items-center justify-center`}>
                                  <IconComponent className={`h-4 w-4 ${colors.text}`} />
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-0.5">
                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${colors.bg} ${colors.text}`}>
                                      {getActivityLabel(notification.action_type, notification.description)}
                                    </span>
                                    {unread && (
                                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                                    )}
                                  </div>

                                  <p className="text-sm text-gray-900 dark:text-gray-100 line-clamp-2">
                                    {notification.description}
                                  </p>

                                  {/* Metadata badges */}
                                  {notification.metadata && (
                                    <div className="flex flex-wrap gap-1 mt-1.5">
                                      {notification.metadata.invoice_number && (
                                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[10px] text-gray-600 dark:text-gray-300">
                                          <Receipt className="h-2.5 w-2.5" />
                                          {notification.metadata.invoice_number}
                                        </span>
                                      )}
                                      {notification.metadata.shipment_number && (
                                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[10px] text-gray-600 dark:text-gray-300">
                                          <Package className="h-2.5 w-2.5" />
                                          {notification.metadata.shipment_number}
                                        </span>
                                      )}
                                      {notification.metadata.total_amount !== undefined && (
                                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[10px] text-gray-600 dark:text-gray-300">
                                          <DollarSign className="h-2.5 w-2.5" />
                                          {typeof notification.metadata.total_amount === 'number' ? `€${notification.metadata.total_amount.toFixed(2)}` : notification.metadata.total_amount}
                                        </span>
                                      )}
                                      {notification.metadata.items_count && (
                                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[10px] text-gray-600 dark:text-gray-300">
                                          {notification.metadata.items_count} items
                                        </span>
                                      )}
                                    </div>
                                  )}

                                  {/* Time and user */}
                                  <div className="flex items-center gap-2 mt-1.5">
                                    <Clock className="h-3 w-3 text-gray-400" />
                                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                                      {formatTimeAgo(notification.created_at)}
                                    </span>
                                    {notification.user && (
                                      <>
                                        <span className="text-gray-300 dark:text-gray-600">|</span>
                                        <span className="text-[10px] text-gray-500 dark:text-gray-400 truncate max-w-[120px]">
                                          {notification.user.full_name || notification.user.email}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
                    <a
                      href="/reports"
                      className="flex items-center justify-center gap-2 w-full px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors"
                      onClick={() => setShowNotifications(false)}
                    >
                      <Activity className="h-4 w-4" />
                      View Full Activity Log
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                aria-haspopup="menu"
                aria-expanded={showUserMenu}
                className="user-menu-button flex items-center space-x-2 lg:space-x-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                {/* User Icon */}
                <div className="w-7 h-7 lg:w-8 lg:h-8 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                  {userIcon ? (
                    <img
                      src={userIcon}
                      alt="Company Logo"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="h-4 w-4 text-gray-400" />
                  )}
                </div>

                <div className="text-left hidden md:block">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {user?.username}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {user?.role}
                  </div>
                </div>
                <ChevronDown className="h-3 w-3 lg:h-4 lg:w-4 text-gray-400 hidden md:block" />
              </button>

              {/* Dropdown Menu */}
              {showUserMenu && (
                <div className="user-menu-dropdown absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                  <div className="py-2">
                    <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {user?.company_name || 'No Company'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {user?.email}
                      </div>
                    </div>

                    {canSwitchCompany && (
                      <div className="py-1 border-b border-gray-200 dark:border-gray-700">
                        <div className="px-4 py-1 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                          Switch company
                        </div>
                        {companies.map((company) => {
                          const isActive = company.id === activeCompanyId;
                          const isSwitching = switchingCompanyId === company.id;
                          return (
                            <button
                              key={company.id}
                              onClick={() => handleSwitchCompany(company.id)}
                              disabled={!!switchingCompanyId}
                              className={`flex items-center justify-between w-full px-4 py-2 text-sm text-left transition-colors disabled:opacity-60 ${
                                isActive
                                  ? 'text-teal-700 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20'
                                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              <span className="flex items-center min-w-0">
                                <Building2 className="h-4 w-4 mr-3 flex-shrink-0" />
                                <span className="truncate">
                                  {company.company_name || company.full_name}
                                </span>
                              </span>
                              {isSwitching ? (
                                <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin" />
                              ) : isActive ? (
                                <Check className="h-4 w-4 flex-shrink-0" />
                              ) : null}
                            </button>
                          );
                        })}
                        {switchError && (
                          <div className="px-4 py-2 text-xs text-red-600 dark:text-red-400">
                            {switchError}
                          </div>
                        )}
                      </div>
                    )}

                    <button
                      onClick={() => {
                        setShowProfileModal(true);
                        setShowUserMenu(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <User className="h-4 w-4 mr-3" />
                      My Profile
                    </button>

                    <a
                      href="/settings"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <Settings className="h-4 w-4 mr-3" />
                      Settings
                    </a>

                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <LogOut className="h-4 w-4 mr-3" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />
    </nav>
  );
};